import type { IntlShape } from "react-intl"
import type { Provider } from "./types"

const MINUTE_MS = 60 * 1000
const HOUR_MS = 60 * MINUTE_MS
const DAY_MS = 24 * HOUR_MS

/**
 * How close an appointment has to be before the Up next card offers `Join` instead of
 * `Complete forms`.
 *
 * Provisional default pending design: frame 4 only draws "In 5 mins" and never states the rule.
 * Kept as a single exported constant so there is one place to change it once design answers.
 */
export const IMMINENT_THRESHOLD_MINUTES = 30

/**
 * True once the appointment is inside the imminent window, including after its start time — a member
 * joining ten minutes late still needs the `Join` action, not a forms prompt.
 */
export const isAppointmentImminent = (date: Date | null | undefined, now: Date = new Date()): boolean =>
  date != null && date.getTime() - now.getTime() <= IMMINENT_THRESHOLD_MINUTES * MINUTE_MS

/**
 * The instant the imminent window opens — the appointment's start minus the threshold.
 *
 * Exists so a caller can schedule work *at* the crossing instead of polling towards it. Derived from
 * the same constant as `isAppointmentImminent`, so the two can never disagree about where the line is.
 */
export const getImminentThresholdInstant = (date: Date | null | undefined): Date | null =>
  date == null ? null : new Date(date.getTime() - IMMINENT_THRESHOLD_MINUTES * MINUTE_MS)

export const formatProviderInitials = (name: string | null | undefined): string => {
  const [firstName, lastName] = (name ?? "").trim().split(" ")
  const firstInitial = firstName?.charAt(0).toUpperCase() ?? ""
  const lastInitial = lastName?.charAt(0).toUpperCase() ?? ""
  return `${firstInitial}${lastInitial}`
}

/**
 * Composes a provider's display name out of the parts the record actually carries.
 *
 * The honorific is data, not something derivable from `name`/`title`: frame 5 draws "Jane Doe, MD"
 * directly beside "Dr. Susanna Park, MD", so the identical `{ name, title: "MD" }` shape has to be
 * able to render both ways. This previously prefixed `Dr. ` unconditionally and got Jane Doe wrong.
 *
 * Provisional pending design/API: the real API may send an honorific, a credential, or a ready-made
 * display name. Whichever it is, the decision belongs to the record and not to this function.
 */
export const formatProviderName = (
  provider: Pick<Provider, "honorific" | "name" | "title"> | null | undefined
): string => {
  const named = [provider?.honorific, provider?.name].filter(Boolean).join(" ")
  return provider?.title ? `${named}, ${provider.title}` : named
}

/**
 * "August 31" — the long form the Up next card and the cancel dialog use.
 *
 * No `timeZone` option anywhere below: an upcoming appointment's `date` is an instant, so the
 * browser's own zone is the member's zone. See the note on `UpcomingAppointment` in `types.ts`.
 *
 * The design writes "August 31st". `Intl.DateTimeFormat` has no ordinal day, and hand-rolling "st /
 * nd / rd / th" would put English grammar back into a formatter this ticket exists to take it out
 * of, so the ordinal is dropped deliberately.
 */
export const formatAppointmentDate = (intl: IntlShape, date: Date): string =>
  intl.formatDate(date, { month: "long", day: "numeric" })

/** "Tue, Aug 25" — the weekday form frame 4 switches to once the visit is imminent. */
export const formatAppointmentDateWithWeekday = (intl: IntlShape, date: Date): string =>
  intl.formatDate(date, { weekday: "short", month: "short", day: "numeric" })

/** "10:30 AM" in the member's own zone. */
export const formatAppointmentTime = (intl: IntlShape, date: Date): string =>
  intl.formatTime(date, { hour: "numeric", minute: "2-digit" })

// Past appointment dates are date-only strings; pin to UTC so they don't shift a day in local time.
export const formatPastAppointmentDate = (intl: IntlShape, date: string): string =>
  intl.formatDate(date, {
    weekday: "short",
    month: "short",
    day: "numeric",
    timeZone: "UTC"
  })

/**
 * The countdown chip: "in 3 days", "in 2 hours", "in 5 minutes".
 *
 * Delegated to `formatRelativeTime` so the unit name and its plural form come from the locale's CLDR
 * data — "in 1 day" vs "in 3 days" is a translation concern, not a string concat. The chip's
 * uppercase rendering comes from `theme.typography.chip`, which is where the design's "IN 3 DAYS"
 * casing comes from rather than from this string.
 *
 * Frame 4 writes "In 5 mins"; en-US CLDR's long form is "in 5 minutes". Abbreviating is the locale's
 * call, not ours, so the long form stands and the copy difference is a question for design.
 */
export const formatCountdownLabel = (intl: IntlShape, date: Date, now: Date = new Date()): string => {
  const differenceMs = date.getTime() - now.getTime()
  const distanceMs = Math.abs(differenceMs)

  if (distanceMs >= DAY_MS) {
    return intl.formatRelativeTime(Math.round(differenceMs / DAY_MS), "day")
  }
  if (distanceMs >= HOUR_MS) {
    return intl.formatRelativeTime(Math.round(differenceMs / HOUR_MS), "hour")
  }
  return intl.formatRelativeTime(Math.round(differenceMs / MINUTE_MS), "minute")
}
